import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
import { updateStay } from '../../../../api/stays-reservations-client';
import type { StayFull } from '../../../../interfaces/stays';
import { AppButton } from '../../../button/app-button';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { StatusPill } from '../../../status-pill/status-pill';
import { ToggleChip } from '../../../toggle-chip/toggle-chip';
import { BodyRow, Td, Th, Tr } from '../../../table/list-table';
import toast from '../../../../utils/toast';
import { stayDates, timingText } from '../../../../utils/stays-stage';
import { clockTime } from '../../../../function-utils/building-time';
import { palette, radius, spacing, status as statusTone, typography } from '../../../../style-utils/theme';
import { RowActions } from '../../dashboard/dashboard-stays-components/dashboard-stays.styled';
import {
  cleaningPill,
  firstOf,
  fullName,
  grossText,
  guestsLabel,
  initialsOf,
  isInHouse,
  isNoShow,
  netText,
  nightsLabel,
  sourceText,
  timingTone,
} from './reservation-labels';
import { JourneyBar } from './journey-bar';
import { ContactLinks } from './contact-links';
import { ReservationActionsMenu, reportStayError } from './reservation-actions-menu';
import { CleaningPill } from '../operations/cleaning-pill';
import { useAuth } from '../../../../context/auth-provider';
import { isCohost } from '../../../../auth/roles';

/**
 * ONE ROW OF THE STAYS TABLE (plan S4, R-S4.25/R-S4.26) — also the Overview's
 * "Next arrivals" row. Guest · Stay · Source · Amount · Status · Actions;
 * below lg the list table reflows it to label:value cards (Td data-label).
 *
 * THREE CLOCKS, NEVER MIXED: check-in / check-out are building DAY KEYS
 * (printed as they are, stayDates); times are building WALL times; the journey
 * bar reads the envelope's building_now. Nothing here builds a Date.
 *
 * Every write control is stays:manage only (`canManage`) — without it the
 * paid state is a read-only pill and there is no Actions column at all.
 */

const Cell = styled.span`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

const GuestCell = styled.span`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.xs};
  min-width: 0;
`;

const Avatar = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${spacing.lg};
  height: ${spacing.lg};
  border-radius: ${radius.circle};
  background: ${palette.tealTint};
  color: ${palette.lagoon};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
`;

const Name = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

const Meta = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

const Tags = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: ${spacing.half};
  min-width: 0;
`;

const Tabular = styled.span`
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
`;

const Badge = styled.span`
  align-self: flex-start;
  padding: 0 ${spacing.xs};
  border-radius: ${radius.pill};
  background: ${palette.surfaceMuted};
  color: ${palette.inkSoft};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${spacing.md};
`;

const Amount = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  font-variant-numeric: tabular-nums;
`;

const Struck = styled.s`
  color: ${palette.slate};
  font-variant-numeric: tabular-nums;
`;

const NotCounted = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${statusTone.neutral.fg};
`;

/** The building wall time the timing chip prints, for its data-time. */
const timingWall = (stay: StayFull): string | undefined => {
  if (stay.archived_at) return undefined;
  if (stay.stage.key === 'departure_day') return stay.effective.departure_time;
  if (stay.stage.key === 'arrival_day' || stay.stage.key === 'upcoming') return stay.effective.arrival_time;

  return undefined;
};

export const reservationColumns =(canManage: boolean): number => (canManage ? 6 : 5);

/** The header row the table and the Overview's "Next arrivals" share. */
export const ReservationHeadRow: React.FC<{ canManage: boolean }> = ({ canManage }) => (
  <Tr>
    <Th scope="col">Guest</Th>
    <Th scope="col">Stay</Th>
    <Th scope="col">Source</Th>
    <Th scope="col">Amount</Th>
    <Th scope="col">Status</Th>
    {canManage ? <Th scope="col">Actions</Th> : null}
  </Tr>
);

export interface ReservationRowProps {
  stay: StayFull;
  /** The row's index within its page (the cascade delay). */
  index: number;
  /** The envelope's building wall clock and day. */
  buildingNow: string;
  buildingToday: string;
  canManage: boolean;
  /** The first upcoming stay of the list: the "Next arrival" tag. */
  nextArrival?: boolean;
  /** Open the edit modal ("Add amounts" opens it at the money). */
  onEdit?: (stay: StayFull, focus?: 'money' | null) => void;
  /** After any write from the row: the stay as it now is, or null once deleted. */
  onChanged?: (next: StayFull | null) => void;
  onArchived?: (stay: StayFull) => void;
  /** Plan S8: open the message composer for this stay. */
  onMessage?: (stay: StayFull) => void;
}

/** Plan S8: the cleaning pill cycles only on a live stay (S4's chk_sr_cleaning_shape, R-S8.15). */
const cleaningLive = (s: StayFull): boolean =>
  !s.archived_at && (s.status === 'confirmed' || s.status === 'checked_in' || s.status === 'checked_out');

export const ReservationRow: React.FC<ReservationRowProps> = ({
  stay,
  index,
  buildingNow,
  buildingToday,
  canManage,
  nextArrival = false,
  onEdit,
  onChanged,
  onArchived,
  onMessage,
}) => {
  const [confirmPaid, setConfirmPaid] = useState(false);
  const paying = useRef(false);
  // IS5: a co-host's stays carry no amounts; the cell says nothing about money.
  const hideMoney = isCohost(useAuth().user?.role);
  const name = fullName(stay);
  const first = firstOf(stay);
  const cleaning = cleaningPill(stay);
  const gross = grossText(stay);
  const net = netText(stay);
  const noShow = isNoShow(stay);
  const paid = stay.money?.paid === true;
  const changed = (next: StayFull | null) => {
    if (onChanged) onChanged(next);
  };

  const togglePaid = async () => {
    if (paying.current) return;
    paying.current = true;
    try {
      const next = await updateStay(stay.id, {
        version: stay.version,
        payment_recorded_cents: paid ? null : stay.money.gross_cents,
      });
      toast.success(paid ? `${first}'s stay is marked as unpaid.` : `${first}'s stay is marked as paid.`);
      changed(next);
    } catch (error) {
      changed(reportStayError(error, 'Could not save the payment. Try again.') || stay);
    } finally {
      paying.current = false;
    }
  };

  return (
    <BodyRow
      index={index}
      data-stay-id={stay.id}
      data-stay-status={stay.status}
      data-stage={stay.stage.key}
      data-revoked={stay.access_revoked || undefined}
    >
      <Td data-label="Guest">
        <GuestCell>
          <Avatar aria-hidden="true">{initialsOf(stay)}</Avatar>
          <Cell>
            <Name>{name}</Name>
            <Meta>{guestsLabel(stay.party_size)}</Meta>
            <Tags>
              {isInHouse(stay) && !stay.archived_at ? (
                <StatusPill tone="success" dot>
                  Currently staying
                </StatusPill>
              ) : null}
              {nextArrival ? (
                <StatusPill tone="brand" dot>
                  Next arrival
                </StatusPill>
              ) : null}
              {stay.access_revoked ? (
                <StatusPill tone="alert" dot>
                  Guest access revoked
                </StatusPill>
              ) : null}
              {stay.status === 'cancelled' ? <StatusPill tone="neutral">Cancelled</StatusPill> : null}
              {stay.status === 'no_show' ? <StatusPill tone="neutral">No-show</StatusPill> : null}
              {/* Plan S5 (screens §3): an imported stay, and one whose booking site gave no name (K-62). */}
              {stay.created_via === 'ical' ? (
                <StatusPill tone="neutral" data-testid="stay-imported">{`From ${sourceText(stay.source)}`}</StatusPill>
              ) : null}
              {stay.guest_name_required ? (
                <StatusPill tone="warning" dot data-testid="stay-name-needed">
                  Guest name needed
                </StatusPill>
              ) : null}
            </Tags>
            <JourneyBar stay={stay} buildingNow={buildingNow} name={name} />
            <ContactLinks contact={stay.contact} name={name} />
          </Cell>
        </GuestCell>
      </Td>
      <Td data-label="Stay">
        <Cell>
          <Tabular data-check-in={stay.check_in} data-check-out={stay.check_out}>
            {stayDates(stay.check_in, stay.check_out)}
          </Tabular>
          <Badge>{nightsLabel(stay.nights)}</Badge>
          <span>
            {canManage && onChanged && cleaningLive(stay) ? (
              <CleaningPill stay={stay} onChanged={next => changed(next)} />
            ) : (
              <StatusPill tone={cleaning.tone} dot>
                {cleaning.text}
              </StatusPill>
            )}
          </span>
        </Cell>
      </Td>
      <Td data-label="Source">{sourceText(stay.source)}</Td>
      <Td data-label="Amount">
        <Cell>
          {hideMoney ? (
            <Meta>—</Meta>
          ) : !gross ? (
            canManage && onEdit ? (
              <span>
                <AppButton
                  variant="ghost"
                  size="compact"
                  aria-label={`Add amounts to ${name}'s stay`}
                  onClick={() => onEdit(stay, 'money')}
                >
                  Add amounts
                </AppButton>
              </span>
            ) : (
              <Meta>—</Meta>
            )
          ) : noShow ? (
            <>
              <Struck>{gross}</Struck>
              <NotCounted>Not counted</NotCounted>
            </>
          ) : (
            <>
              <Amount>{gross}</Amount>
              {net ? <Meta>{net}</Meta> : null}
              <span>
                {canManage ? (
                  <ToggleChip
                    pressed={paid}
                    aria-label={`${paid ? 'Paid' : 'Unpaid'}, ${name}'s stay`}
                    onToggle={() => setConfirmPaid(true)}
                  >
                    {paid ? 'Paid' : 'Unpaid'}
                  </ToggleChip>
                ) : (
                  <StatusPill tone={paid ? 'success' : 'neutral'}>{paid ? 'Paid' : 'Unpaid'}</StatusPill>
                )}
              </span>
            </>
          )}
        </Cell>
      </Td>
      <Td data-label="Status">
        <StatusPill tone={timingTone(stay)} data-time={timingWall(stay)}>
          {timingText(stay)}
        </StatusPill>
        {/* IS7: another account's desk signed this guest in. On the place's clock. */}
        {stay.desk_arrived_at ? (
          <Meta data-testid="stay-desk-arrived" data-at={stay.desk_arrived_at}>
            {`Arrived at the desk ${clockTime(stay.desk_arrived_at, stay.building_timezone)}`}
          </Meta>
        ) : null}
      </Td>
      {canManage ? (
        <Td data-label="Actions">
          <RowActions>
            {onEdit ? (
              <AppButton variant="secondary" size="compact" aria-label={`Edit ${name}'s stay`} onClick={() => onEdit(stay, null)}>
                Edit
              </AppButton>
            ) : null}
            {onMessage ? (
              <AppButton variant="secondary" size="compact" aria-label={`Message ${name}`} onClick={() => onMessage(stay)}>
                Message
              </AppButton>
            ) : null}
            <ReservationActionsMenu stay={stay} buildingToday={buildingToday} onChanged={changed} onArchived={onArchived} />
          </RowActions>
          <ConfirmDialog
            open={confirmPaid}
            title={paid ? `Mark ${first}'s stay as unpaid?` : `Mark ${first}'s stay as paid?`}
            message={
              paid
                ? 'The recorded payment is cleared. Nothing is refunded: this is your own record.'
                : `${gross} is recorded as received. Nothing is charged: this is your own record.`
            }
            primaryLabel={paid ? 'Mark as unpaid' : 'Mark as paid'}
            dismissLabel="Never mind"
            onPrimary={async () => {
              await togglePaid();
              setConfirmPaid(false);
            }}
            onDismiss={() => setConfirmPaid(false)}
          />
        </Td>
      ) : null}
    </BodyRow>
  );
};
